import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  FollowUp,
  FollowUpStatus,
  FollowUpsUser,
} from '@app/modules/follow-ups/follow-ups.types';
import { FollowUpsUtilsService } from '@app/modules/follow-ups/services/follow-ups-utils.service';
import { GetFollowUpsListAction } from '@app/modules/follow-ups/store/follow-ups.actions';
import { FollowUpsState } from '@app/modules/follow-ups/store/follow-ups.state';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableDataType,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { DisplayNamePipe } from '@shared/pipes/display-name.pipe';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-follow-ups-list-dialog',
  imports: [DataTableComponent, ReactiveFormsModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './follow-ups-list-dialog.component.html',
})
export class FollowUpsListDialogComponent implements OnInit {
  private store = inject(Store);
  private modalRef = inject(NgbActiveModal);
  private followUpUtils = inject(FollowUpsUtilsService);

  @Input() followUpIds: string[];

  @ViewChild('statusTemplate', { static: true })
  statusTemplate: TemplateRef<any>;

  followUps$: Observable<FollowUp[]>;
  users: FollowUpsUser[] = [];

  dataTableConfiguration: DataTableConfiguration;

  ngOnInit() {
    this.users = this.store.selectSnapshot(FollowUpsState.getFollowUpsUsers);
    this.followUps$ = this.store
      .dispatch(
        new GetFollowUpsListAction({ ids: this.followUpIds }, {}, null, true)
      )
      .pipe(
        switchMap(() => this.store.select(FollowUpsState.getFollowUpsByIds)),
        map((getByIds) => getByIds(this.followUpIds))
      );

    this.dataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.MULTI,
      actionsLocation: DataTableActionsLocation.TOP,
      columns: [
        {
          label: 'followUps.list.columns.referenceKey',
          property: 'referenceKey',
        },
        {
          label: 'followUps.list.columns.type',
          property: 'type',
          translationPrefix: 'followUps.types.',
        },
        {
          label: 'followUps.list.columns.reason',
          property: 'reason',
          translationPrefix: 'followUps.reasons.',
        },
        {
          label: 'followUps.list.columns.reporter',
          invoke: {
            objectReference: {
              getName: (followUp: FollowUp) =>
                new DisplayNamePipe().transform(
                  followUp.generatedBy,
                  this.users
                ),
            },
            name: 'getName',
            args: [],
          },
        },
        {
          label: 'followUps.list.columns.assignee',
          invoke: {
            objectReference: {
              getName: (followUp: FollowUp) =>
                new DisplayNamePipe().transform(followUp.assignee, this.users),
            },
            name: 'getName',
            args: [],
          },
        },
        {
          label: 'followUps.list.columns.link',
          property: 'link.type',
          translationPrefix: 'followUps.linkTypes.',
        },
        {
          label: 'followUps.list.columns.notes',
          property: 'notes',
        },
        {
          label: 'followUps.list.columns.statusUpdated',
          property: 'lastStatusChanged',
          type: DataTableDataType.DATETIME,
        },
        {
          label: 'followUps.list.columns.status',
          propertyTemplate: this.statusTemplate,
        },
      ],
      singleActions: [
        {
          name: 'followUps.list.actions.details',
          icon: 'fa-light fa-file',
          action: this.followUpUtils.openDetails.bind(this.followUpUtils),
          type: DataTableActionType.BUTTON,
        },
        {
          name: 'followUps.list.actions.setNotes',
          icon: 'fa-light fa-note-sticky',
          action: this.followUpUtils.setNotes.bind(this.followUpUtils),
          type: DataTableActionType.BUTTON,
        },
      ],
      massActions: [
        {
          name: 'followUps.list.actions.setAssignee',
          icon: 'fa-light fa-user-plus',
          disabled: (followUps: any[]) => !followUps.length,
          action: this.followUpUtils.setAssignee.bind(this.followUpUtils),
          type: DataTableActionType.BUTTON,
        },
        {
          name: 'followUps.list.actions.resolve',
          icon: 'fa-light fa-check',
          disabled: this.followUpUtils.canResolveFollowUps,
          action: this.followUpUtils.resolveFollowUps.bind(this.followUpUtils),
          type: DataTableActionType.BUTTON,
        },
        {
          name: 'followUps.list.actions.archive',
          icon: 'fa-light fa-box-archive',
          disabled: (followUps: any[]) => !followUps.length,
          action: this.followUpUtils.archiveFollowUps.bind(this.followUpUtils),
          type: DataTableActionType.BUTTON,
        },
      ],
      rows$: this.followUps$,
    };
  }

  close() {
    this.modalRef.close();
  }

  protected readonly FollowUpStatus = FollowUpStatus;
}
